import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrivacyPolicy = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to load site settings:', error);
        // Use defaults if fetch fails
        setSettings({
          company_name: "JAMOH & CO AUTOS LTD",
          office_address: "Plot 123, Victoria Island, Lagos, Nigeria",
          primary_email: "info@jamohautos.com",
          phone_number: "+234-800-JAMOH (52664)"
        });
      }
    };
    fetchSettings();
  }, []);

  if (!settings) {
    return null; // or loading skeleton
  }

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center space-x-2 text-textSecondary hover:text-primary mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Home</span>
        </Link>

        <div className="glass p-8 rounded-sm">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-heading font-bold">Privacy Policy</h1>
              <p className="text-textSecondary text-sm">Last updated: January 15, 2026</p>
            </div>
          </div>

          <div className="space-y-6 text-textSecondary">
            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">1. Introduction</h2>
              <p className="leading-relaxed">
                {settings.company_name} ("we", "us", "our") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our online auction platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">2. Information We Collect</h2>
              <p className="leading-relaxed mb-3">
                We collect several types of information:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Personal Information:</strong> Name, email address, phone number, physical address</li>
                <li><strong>Account Information:</strong> Username, password, profile picture</li>
                <li><strong>Payment Information:</strong> Credit card details, bank account information (processed securely through Stripe)</li>
                <li><strong>Bidding History:</strong> Bids placed, auctions participated in, purchase history</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information, cookies</li>
                <li><strong>Communication Data:</strong> Messages, support inquiries, feedback</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-3">
                We use your information to:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Provide and maintain our auction services</li>
                <li>Process your bids and purchases</li>
                <li>Send you auction updates and notifications</li>
                <li>Verify your identity and prevent fraud</li>
                <li>Improve our platform and user experience</li>
                <li>Comply with legal obligations</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Provide customer support</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">4. Information Sharing</h2>
              <p className="leading-relaxed mb-3">
                We may share your information with:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Payment Processors:</strong> Stripe for secure payment processing</li>
                <li><strong>Shipping Partners:</strong> For vehicle delivery and logistics</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                <li><strong>Service Providers:</strong> Third parties who assist in platform operations</li>
                <li><strong>Sellers/Dealers:</strong> Limited information necessary for transaction completion</li>
              </ul>
              <p className="leading-relaxed mt-3">
                We do not sell your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">5. Data Security</h2>
              <p className="leading-relaxed">
                We implement industry-standard security measures to protect your data, including:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4 mt-3">
                <li>SSL encryption for data transmission</li>
                <li>Secure password hashing</li>
                <li>Regular security audits</li>
                <li>Access controls and authentication</li>
                <li>Secure payment processing through PCI-compliant providers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">6. Cookies and Tracking</h2>
              <p className="leading-relaxed">
                We use cookies and similar tracking technologies to enhance your experience. Cookies help us remember your preferences, analyze usage patterns, and provide personalized content. You can control cookie settings through your browser.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">7. Your Rights</h2>
              <p className="leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Access your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data</li>
                <li>Object to data processing</li>
                <li>Withdraw consent for marketing communications</li>
                <li>Export your data</li>
                <li>Lodge a complaint with regulatory authorities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">8. Data Retention</h2>
              <p className="leading-relaxed">
                We retain your personal data for as long as necessary to provide our services and comply with legal obligations. Account information is retained while your account is active. Transaction records are kept for 7 years for tax and legal purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">9. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our platform is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected data from a minor, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">10. International Data Transfers</h2>
              <p className="leading-relaxed">
                Your data may be transferred to and processed in countries outside Nigeria, including the United States. We ensure appropriate safeguards are in place to protect your data in accordance with this privacy policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">11. Third-Party Links</h2>
              <p className="leading-relaxed">
                Our platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">12. Email Communications</h2>
              <p className="leading-relaxed">
                We send transactional emails (bid notifications, purchase confirmations) and marketing emails. You can unsubscribe from marketing emails at any time using the unsubscribe link. Transactional emails are necessary for service delivery and cannot be opted out of.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">13. Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this privacy policy from time to time. Changes will be posted on this page with an updated revision date. Significant changes will be communicated via email or platform notification.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">14. Contact Us</h2>
              <p className="leading-relaxed">
                For questions about this privacy policy or to exercise your rights, contact us at:
                <br /><br />
                <strong>{settings.company_name}</strong><br />
                Data Protection Officer<br />
                {settings.office_address}<br />
                Email: {settings.primary_email}<br />
                Phone: {settings.phone_number}
              </p>
            </section>

            <section className="bg-surfaceHighlight p-4 rounded-sm mt-6">
              <p className="text-sm">
                <strong>Your Consent:</strong> By using our platform, you consent to this privacy policy and agree to its terms.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;